//@ts-nocheck
import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps } from '../common/props';
import { Icon } from '../icon/Icon';
import { AppMenuProps, NavMenu } from './NavMenu';
import { UserMenu, UserMenuProps } from './UserMenu';
import { Space } from '../space/Space';
import { Divider } from '../divider/Divider';
import { MenuItem } from '../menu/MenuItem';
import { Menu } from '../menu/Menu';
import { About } from './About';
import { useAppConfig } from '@mc/react-app-base';
import { NavbarButton } from '../button/NavbarButton';
import { mcHelp, mcMenuHamburger, mcClose } from '@mc/react-ui-icons';
import { Modal } from '../modal/Modal';
import { isMobile } from '../common/utils';

export interface INavbarProps extends BaseProps {}

export type NavbarProps = INavbarProps & {
  icon?: React.ReactNode;
  userMenu?: UserMenuProps;
  togglePin?: () => void;
  pinned?: boolean;
  /** Pokud true, navbar je v minimalizovaném módu (viewport < 700px, ale ne mobilní). Klik otevírá popover místo připínání. */
  isMinimizedMode?: boolean;
} & AppMenuProps;

export const NAVBAR_DEFAULT_WIDTH = 212;

/**
 * Navbar pro aplikaci.
 */
export const Navbar: React.FC<NavbarProps> = ({
  icon,
  pinned,
  className,
  style,
  children,
  menuItems,
  userMenu,
  onClick,
  currentKey,
  togglePin,
  panelWidth = NAVBAR_DEFAULT_WIDTH,
  isMinimizedMode,
}) => {
  let cls = classNames(Classes.NAVBAR, className, {});
  const [isOpen, setIsOpen] = React.useState(false);
  const buttonRef = React.useRef(null);
  const appConfig = useAppConfig();
  const toggle = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMobile || isMinimizedMode) {
      // Mobilní a minimalizovaný mód: otevřít/zavřít pouze jako popover, nepřipínat
      setIsOpen(!isOpen);
    } else {
      togglePin();
      setIsOpen(pinned ? false : !isOpen);
    }
  };
  const showNapoveda = React.useCallback(() => {
    window.open(`${appConfig.serverContextPath}/napoveda`, '_blank');
  }, []);
  const onClose = () => setIsOpen(false);

  const mergedUserMenuProps: UserMenuProps = {
    ...(userMenu || {}),
    items: [
      ...(userMenu?.items || []),
      ...(isMobile
        ? [
            !userMenu?.hideNapovedaMenuItem && (
              <MenuItem onClick={showNapoveda} text={'Nápověda'} />
            ),
            ...(userMenu?.helpItems || []),
            <MenuItem
              onClick={() =>
                Modal.show(({ onClose }) => (
                  <About icon={icon} onClose={onClose} />
                ))
              }
              text={'O aplikaci'}
            />,
          ]
        : []),
    ],
  };
  return (
    <div className={cls} style={style}>
      {menuItems?.length > 0 && (
        <>
          <div
            ref={buttonRef}
            className={Classes.NAVBAR_MENU_ICON}
            onMouseLeave={() => !isMobile && !isMinimizedMode && setIsOpen(false)}
          >
            <NavbarButton
              onMouseEnter={() => !isMobile && !isMinimizedMode && setIsOpen(true)}
              style={'link'}
              onClick={(e) => toggle(e)}
              icon={<Icon fixedWidth icon={mcMenuHamburger} />}
            />
            {isOpen && !pinned && (
              <PopOverNavMenu
                onClick={onClick}
                menuItems={menuItems}
                togglePin={togglePin}
                buttonRef={buttonRef}
                onClose={onClose}
                currentKey={currentKey}
                panelWidth={panelWidth}
                showShadow={!pinned}
              />
            )}
          </div>
        </>
      )}
      <div className={Classes.NAVBAR_CONTENT}>{children}</div>
      <div className={Classes.NAVBAR_EXTRA}>
        <Space className={'w-100'}>
          {!isMobile && (
            <NavbarButton
              style={'link'}
              menu={
                <Menu>
                  {!userMenu?.hideNapovedaMenuItem && (
                    <MenuItem onClick={showNapoveda} text={'Nápověda'} />
                  )}
                  {userMenu?.helpItems}
                  <MenuItem
                    onClick={() =>
                      Modal.show(({ onClose }) => (
                        <About icon={icon} onClose={onClose} />
                      ))
                    }
                    text={'O aplikaci'}
                  />
                </Menu>
              }
              icon={<Icon icon={mcHelp} />}
            />
          )}
          <UserMenu {...mergedUserMenuProps} />
        </Space>
      </div>
    </div>
  );
};

const isUnderEl = (parentEl, el) => {
  while (el !== null) {
    if (el === parentEl) {
      return true;
    }
    el = el.parentNode;
  }
  return false;
};
const PopOverNavMenu: React.FC<
  {
    onClose: () => {};
    buttonRef: React.RefObject<HTMLButtonElement>;
    togglePin?: () => void;
    pinned?: boolean;
    showShadow?: boolean;
  } & AppMenuProps
> = ({
  onClose,
  buttonRef,
  togglePin,
  currentKey,
  onClick,
  menuItems,
  panelWidth,
  showShadow,
}) => {
  const vyskaContainer = document.getElementById('containerVyskaId');
  //TODO překopat a nahradit za useRef
  const [vyska, setVyska] = React.useState(0);
  React.useEffect(() => {
    isMobile
      ? setVyska(window.innerHeight)
      : setVyska(vyskaContainer?.clientHeight - 14);
  }, [vyskaContainer?.clientHeight]);
  const onMouseClick = (e: MouseEvent) => {
    //TODO asi by to chtělo vymyslet líp
    if (
      e.target !== containerRef.current &&
      e.target.id !== 'nav-menu-item-container'
    ) {
      onClose();
    }
  };
  const onMouseDown = (e: MouseEvent) => {
    if (!isUnderEl(buttonRef.current, e.target)) {
      if (!isUnderEl(containerRef.current, e.target)) {
        onClose();
      }
    }
  };
  const onResize = () => {
    onClose();
  };

  const containerRef = React.useRef('');
  React.useEffect(() => {
    document.addEventListener('mousedown', onMouseDown, false);
    document.addEventListener('click', onMouseClick, false);
    window.addEventListener('resize', onResize, false);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, false);
      document.removeEventListener('click', onMouseClick, false);
      window.removeEventListener('resize', onResize, false);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id={'popover-nav-menu'}
      tabIndex={0}
      style={{
        maxHeight: vyska,
        minWidth: panelWidth,
      }}
      className={classNames(
        Classes.NAVBAR_MENU_CONTAINER,
        Classes.NAVBAR_MENU_POPOVER,
        {
          [`${Classes.NAVBAR_MENU_POPOVER_SHOW_SHADOW}`]: showShadow,
        }
      )}
    >
      {/* Zavírací tlačítko na mobil */}
      <div className={Classes.NAVBAR_MENU_CLOSE_BUTTON}>
        <NavbarButton
          style={'link'}
          icon={<Icon fixedWidth icon={mcClose} size={'lg'} />}
          onClick={onClose}
          title="Zavřít menu"
        />
      </div>
      <NavMenu
        onClick={onClick}
        currentKey={currentKey}
        pinned={false}
        togglePin={togglePin}
        menuItems={menuItems}
        style={{
          maxHeight: vyska,
        }}
      />
    </div>
  );
};
